import { useState } from 'react';
import { Download, FileSpreadsheet, FileType, FileText } from 'lucide-react';
import { Button, Dropdown, MenuItem, message } from '../ui';
import { api } from '../api/client';

export type ExportFormat = 'pdf' | 'xlsx' | 'csv';

/** Download a binary export (xlsx / pdf). The endpoints send raw bytes via `@Res`, so `responseType`
 *  must be 'blob' — 'text' would corrupt the file, and the default JSON parse would reject it. */
async function downloadBinary(url: string, filename: string, mime: string): Promise<void> {
  const r = await api.get(url, { responseType: 'blob' });
  const objectUrl = URL.createObjectURL(new Blob([r.data], { type: mime }));
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  // Revoke on a timer, not immediately: Chrome needs the URL to survive long enough to start the
  // download, and an un-revoked blob is a memory leak that survives the page.
  setTimeout(() => { URL.revokeObjectURL(objectUrl); a.remove(); }, 100);
}

const META: Record<ExportFormat, { label: string; icon: JSX.Element; mime: string }> = {
  pdf: { label: 'PDF', icon: <FileType className="size-4" />, mime: 'application/pdf' },
  xlsx: { label: 'Excel', icon: <FileSpreadsheet className="size-4" />, mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  /*
   * CSV is offered ONLY where a screen genuinely has no PDF worth producing.
   *
   * The bank reconciliation is the case it exists for: the comparison is nineteen columns wide, so on
   * a page every cell would clip — and something downstream still has to be able to ingest the rows.
   * Everywhere else the pair is PDF (to file or email) and Excel (money as real numbers), and adding
   * CSV there would just be a third copy of the same data with the formatting stripped out.
   */
  csv: { label: 'CSV', icon: <FileText className="size-4" />, mime: 'text/csv' },
};

export interface ExportMenuProps {
  /** Endpoint WITHOUT its extension — the format is appended (`…/bank-summary` → `…/bank-summary.pdf`). */
  baseUrl: string;
  /** Query string already encoded, without the leading `?`. */
  query?: string;
  /** Downloaded filename WITHOUT its extension. */
  filename: string;
  /** Which formats to offer, in menu order. */
  formats?: ExportFormat[];
  label?: string;
  /** Icon-only trigger, for use inside a table row. */
  iconOnly?: boolean;
  size?: 'sm' | 'md';
  variant?: 'default' | 'primary' | 'text';
  title?: string;
}

/**
 * One export control offering the same data in several formats.
 *
 * Every export here used to be a single button hard-wired to CSV, which meant anyone who wanted a
 * document to file or attach had to open the CSV and re-save it. The two formats are not
 * interchangeable: PDF is the one you file or email, Excel keeps money as real numbers with a number
 * format so a column sums without cleanup.
 */
export function ExportMenu({
  baseUrl, query, filename, formats = ['pdf', 'xlsx'],
  label = 'Export', iconOnly, size = 'sm', variant = 'default', title,
}: ExportMenuProps) {
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const run = async (fmt: ExportFormat) => {
    if (busy) return;
    setBusy(fmt);
    try {
      const url = `${baseUrl}.${fmt}${query ? `?${query}` : ''}`;
      await downloadBinary(url, `${filename}.${fmt}`, META[fmt].mime);
    } catch (e: any) {
      message.error(e?.response?.data?.error?.message ?? `Could not export as ${META[fmt].label}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dropdown
      align="end"
      panel={formats.map((f) => (
        <MenuItem key={f} icon={META[f].icon} disabled={!!busy} onClick={() => run(f)}>
          {busy === f ? `Preparing ${META[f].label}…` : META[f].label}
        </MenuItem>
      ))}
    >
      <Button
        size={size}
        variant={variant}
        loading={!!busy}
        icon={<Download className={size === 'sm' ? 'size-4' : 'size-4'} />}
        aria-label={title ?? label}
        title={title ?? label}
      >
        {iconOnly ? undefined : label}
      </Button>
    </Dropdown>
  );
}
